import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import AddNewLocation from "../components/AddNewLocation";
import LocationDrawer from "../components/LocationDrawer";
import { GetDoctorDetails } from "../../../../services/StakeholderApi";
import { FetchStakeholderContactByUserId } from "../../../../services/DoctorScheduleAPI";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import AssociatedLocationsMap from "./AssociatedLocationsMap";
import SearchInput from "../../../../common/InlineSearch";
import { QME_DOCTOR_ACTIONS } from "../../../../common/EventConstant";

interface Location {
  locationname: string;
  locationaddress: string;
  email: string;
  phone: string;
  fax: string;
  latitude: number | null;
  longitude: number | null;
}

interface DoctorDetailsProps {}

interface BasicDetailsProps {
  QMEDoctor: string | number | null;
}

const DoctorDetailsCard: React.FC<BasicDetailsProps> = ({ QMEDoctor }) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [doctorName, setDoctorName] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [experience, setExperience] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const userID = useSelector((state: RootState) => state.auth.userid);
  const [showMap, setShowMap] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const searchInputRef = useRef<any>(null);
  const userRole = localStorage.getItem("userrole");

  const allowedFeatures = useSelector(
    (state: RootState) => state.featureAccess.allowedFeatures
  );

  const isAddLocation = allowedFeatures.has(QME_DOCTOR_ACTIONS.ADD_LOCATION);
  const isViewLocation = allowedFeatures.has(
    QME_DOCTOR_ACTIONS.VIEW_LOCATION_MAP
  );

  const fetchDoctorDetails = async () => {
    try {
      const response = await FetchStakeholderContactByUserId(userID);
      const stakeholderContactId = response.result?.stakeholderContactId;
      const allowedRoles = [
        "Case Coordinator",
        "Practice Manager (PMC Staff)",
        "Record Reviewer",
        "Editor",
        "Transcriber",
        "Historian",
        "Billing Specialist",
        "App Admin",
        "Medical Assistant",
      ];

      const isAllowed = userRole !== null && allowedRoles.includes(userRole);

      const data = await GetDoctorDetails(
        isAllowed ? QMEDoctor : stakeholderContactId
      );

      setDoctorName(data?.doctorName?.trim() || "NA");
      setSpecialization(data?.specialization || "NA");
      setExperience(data?.experienceInYears || "NA");

      const rawLocationJson = data?.associatedLocations || [];
      const locationList: Location[] = rawLocationJson
        ?.filter((loc: any) => loc?.locationname)
        ?.map((loc: any) => ({
          fax: loc?.faxnumber || "NA",
          phone: loc?.phonenumber || "NA",
          email: loc?.emailaddress || "NA",
          locationname: loc?.locationname || "NA",
          locationaddress: loc?.locationaddress || "NA",
          latitude: loc?.latitude || null,
          longitude: loc?.longitude || null,
        }));

      setLocations(locationList);
      setFilteredLocations(locationList);
      setExpandedRows({});
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to fetch doctor details:", error);
    }
  };

  useEffect(() => {
    fetchDoctorDetails();
  }, []);

  useEffect(() => {
    if (QMEDoctor) {
      fetchDoctorDetails();
    }
  }, [QMEDoctor]);

  const searchKeys = [
    "locationname",
    "locationaddress",
    "email",
    "phone",
    "fax",
  ];

  const handleSearch = (query: string) => {
    if (!query) {
      setFilteredLocations(locations);
      return;
    }

    const searchLower = query.toLowerCase();
    const filtered = locations.filter((location) =>
      searchKeys.some((key) =>
        String(location[key as keyof Location])
          .toLowerCase()
          .includes(searchLower)
      )
    );

    setFilteredLocations(filtered);
    setExpandedRows({});
  };

  const handleReset = () => {
    if (searchInputRef.current) {
      searchInputRef.current.clear();
    }
    setFilteredLocations(locations);
    setExpandedRows({});
  };

  const toggleExpandRow = (rowIndex: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  // Group locations into rows of 4 cards each
  const groupedLocations: Location[][] = [];
  for (let i = 0; i < filteredLocations.length; i += 4) {
    groupedLocations.push(filteredLocations.slice(i, i + 4));
  }
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <>
      <Container fluid className="p-1">
        <Row>
          <Col md={12}>
            <div className={`fixed_info_basic ${isScrolled ? "scrolled" : ""}`}>
              <h5 className="d-flex align-items-center border-bottom">
                <div className="Container px-1">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="d-flex align-items-center fade-in-left">
                        <span className="me-3" style={{ lineHeight: "47px" }}>
                          Associated Locations
                        </span>
                        {doctorName && (
                          <span className="doctorDetails_badge">
                            <span className="material-symbols-outlined fs-5 sdf theme-color me-2">
                              Badge
                            </span>
                            <span>{doctorName}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center ms-auto">
                  {!showMap && (
                    <div className="d-flex search_closer align-items-center me-2">
                      <div style={{ width: "100%" }} className="gridsearch">
                        <SearchInput
                          ref={searchInputRef}
                          data={locations}
                          searchKeys={searchKeys}
                          onSearchResult={setFilteredLocations}
                          placeholder="Search"
                          onSearchQueryChange={handleSearch}
                          onInputChange={handleSearch}
                        />
                      </div>
                      <button
                        onClick={handleReset}
                        className="reset_btn"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          marginTop: "4px",
                        }}
                      >
                        <span className="material-icons reset_ic0">
                          restart_alt
                        </span>
                      </button>
                    </div>
                  )}
                  <div className="d-flex align-items-center">
                    {locations?.length > 0 && isViewLocation && (
                      <>
                        {showMap ? (
                          <Button
                            variant="primary"
                            className="d-flex align-items-center me-2"
                            onClick={() => {
                              setShowMap(false);
                              handleReset();
                            }}
                          >
                            <span className="material-symbols-outlined">
                              view_agenda
                            </span>
                            <span className="ms-1">List</span>
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            className="d-flex align-items-center btn-text-border p-1 me-2"
                            onClick={() => {
                              setShowMap(true);
                              handleReset();
                            }}
                          >
                            <img
                              src="/map.png"
                              alt="map"
                              style={{ width: "30px", height: "23px" }}
                            />
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      variant="primary"
                      className="d-flex align-items-center"
                      onClick={() => setShowDrawer(true)}
                      disabled={!isAddLocation}
                    >
                      <span className="material-symbols-outlined">
                        add_circle
                      </span>
                      <span className="ms-1">Location</span>
                    </Button>
                  </div>
                </div>
              </h5>
            </div>

            {!showMap ? (
              <div className="m-0 mt-1 carddetail p-3">
                {groupedLocations?.length > 0 ? (
                  groupedLocations.map((rowLocations, rowIndex) => (
                    <Row key={rowIndex} className="mb-3">
                      {rowLocations.map((loc, cardIndex) => (
                        <Col md={3} key={`${rowIndex}-${cardIndex}`}>
                          <Card
                            className={`h-100 shadow-sm border-0 bg-white fade-in-left ${
                              selectedCard === `${rowIndex}-${cardIndex}`
                                ? "selected-card"
                                : ""
                            }`}
                            onClick={() => {
                              toggleExpandRow(rowIndex);
                              setSelectedCard(`${rowIndex}-${cardIndex}`);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <Card.Body className="d-flex flex-column basic_deata">
                              <div>
                                <Card.Text>
                                  <ul style={{ padding: "0px", margin: "0px" }}>
                                    <li className="text-uppercase d-flex align-items-center">
                                      <span className="material-symbols-outlined fs-5 sdf theme-color me-2">
                                        medical_services
                                      </span>
                                      <span> {loc.locationname}</span>
                                    </li>
                                    <li className="d-flex align-items-center">
                                      <span className="material-symbols-outlined fs-5 sdf theme-color me-2 fs-6">
                                        near_me
                                      </span>
                                      <span>{loc.locationaddress}</span>
                                    </li>

                                    <li
                                      className={`expandable-content ${
                                        expandedRows[rowIndex] ? "open" : ""
                                      }`}
                                    >
                                      <div>
                                        <span className="material-symbols-outlined fs-5 sdf theme-color me-2 fs-6">
                                          mail
                                        </span>
                                        <a
                                          href={`mailto:${loc.email}`}
                                          className="text-decoration-none text-dark"
                                        >
                                          <span> {loc.email}</span>
                                        </a>
                                      </div>
                                      <div>
                                        <span className="material-symbols-outlined fs-5 sdf theme-color me-2 fs-6">
                                          call
                                        </span>
                                        <span>{loc.phone}</span>
                                      </div>
                                      <div>
                                        <span className="material-symbols-outlined fs-5 sdf theme-color me-2 fs-6">
                                          print
                                        </span>
                                        <span> {loc.fax}</span>
                                      </div>
                                    </li>
                                  </ul>
                                </Card.Text>
                              </div>
                              <div className="mt-auto">
                                <Button
                                  variant="link"
                                  className="p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpandRow(rowIndex);
                                  }}
                                >
                                  <span className="material-symbols-outlined">
                                    {expandedRows[rowIndex]
                                      ? "collapse_all"
                                      : "expand_all"}
                                  </span>
                                  {expandedRows[rowIndex] ? "" : ""}
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ))
                ) : (
                  <Row>
                    <Col>
                      <div className="text-center py-1 no_rec_data ">
                        <span className="newThingsNod">
                          {" "}
                          No Associated Locations Found
                        </span>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            ) : (
              <AssociatedLocationsMap
                key={`map-${refreshKey}`}
                locations={locations}
              />
            )}
          </Col>
        </Row>
      </Container>
      <LocationDrawer
        title={"Location Details"}
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        placement="end"
      >
        <AddNewLocation
          onClose={() => setShowDrawer(false)}
          onSuccess={fetchDoctorDetails}
          QMEDoctor={QMEDoctor}
        />
      </LocationDrawer>
    </>
  );
};

export default DoctorDetailsCard;
